/**
 * Матрица «раздел базы × роль» (HUB 2.0, W5) — чистая логика без React.
 *
 * Модель доступа раздела (wiki_blocks): visibleToRoles — кто ВИДИТ (пусто —
 * все роли), writeRoles — кто ПИШЕТ из авторов с правом kb:create (пусто —
 * любой автор). Уровень «Публикация» хранится в правах CRM (kb:publish) и
 * здесь только показывается.
 */

export type AccessLevel = 0 | 1 | 2;

export interface MatrixBlock {
  visibleToRoles: string[];
  writeRoles: string[];
}

export interface MatrixRole {
  code: string;
  /** Может ли роль вообще писать статьи (kb:create / kb:edit_any / *). */
  canAuthor: boolean;
}

/** Текущий уровень роли в разделе: 0 — не видит, 1 — просмотр, 2 — редактирование. */
export function accessLevel(block: MatrixBlock, role: MatrixRole): AccessLevel {
  const sees = block.visibleToRoles.length === 0 || block.visibleToRoles.includes(role.code);
  if (!sees) return 0;
  const writes = role.canAuthor && (block.writeRoles.length === 0 || block.writeRoles.includes(role.code));
  return writes ? 2 : 1;
}

/** Следующий уровень по клику: Нет → Просмотр → Редактирование (если роль автор) → Нет. */
export function nextLevel(cur: AccessLevel, role: MatrixRole): AccessLevel {
  if (cur === 0) return 1;
  if (cur === 1) return role.canAuthor ? 2 : 0;
  return 0;
}

/**
 * Поставить роли уровень в разделе. allRoles — вся вселенная ролей (CRM +
 * iiko): «пусто = все» при первом ограничении раскрывается в явный список,
 * чтобы остальные роли не потеряли доступ. Если в итоге отмечены все роли —
 * снова пустой список (= все, включая будущие).
 */
export function applyLevel(block: MatrixBlock, role: MatrixRole, level: AccessLevel, allRoles: MatrixRole[]): MatrixBlock {
  const all = allRoles.map((r) => r.code);
  const authors = allRoles.filter((r) => r.canAuthor).map((r) => r.code);
  let vis = block.visibleToRoles.length ? [...block.visibleToRoles] : [...all];
  let wr = block.writeRoles.length ? [...block.writeRoles] : [...authors];

  if (level === 0) {
    vis = vis.filter((c) => c !== role.code);
    wr = wr.filter((c) => c !== role.code);
  } else {
    if (!vis.includes(role.code)) vis.push(role.code);
    if (level === 2) {
      if (role.canAuthor && !wr.includes(role.code)) wr.push(role.code);
    } else {
      wr = wr.filter((c) => c !== role.code);
    }
  }
  const sameSet = (xs: string[], ys: string[]): boolean => ys.every((y) => xs.includes(y));
  return {
    visibleToRoles: sameSet(vis, all) ? [] : vis.sort(),
    writeRoles: authors.length && sameSet(wr, authors) ? [] : wr.sort(),
  };
}

/** Изменился ли доступ раздела (порядок кодов не важен). */
export function blockChanged(a: MatrixBlock, b: MatrixBlock): boolean {
  const eq = (x: string[], y: string[]): boolean => x.length === y.length && [...x].sort().join('|') === [...y].sort().join('|');
  return !eq(a.visibleToRoles, b.visibleToRoles) || !eq(a.writeRoles, b.writeRoles);
}
